import { useMemo, useEffect, useState } from "react";
import AppSDK from "@growthos/ui-app-sdk";
import Dropdown from "@acadia-ui/dropdown";
import KeyValue, { KeyValueSet, KEY_VALUE_SET_LAYOUT, KEY_VALUE_SET_DISPLAY } from "@acadia-ui/key-value";
import BookFiltersStore from "../../Flux/Stores/BookFiltersStore";
import { TaxonomyCellRenderer } from "../ViewMasterCampaigns/utils";
import { PlatformStatusIcon } from "../../Components/Partials/Icons/PlatformStatusIcon";
import MediaMath from "../../Helpers/MediaMathHelpers";

const classNameComponent = `gos-workbook__campaign-information-ribbon`;
const classNamePrefix = `${classNameComponent}__`;

const DEBUG_MODE = window.LOCAL_DEBUG_MODE || false;

const SuspensefulKeyValue = ({ value, forceResolve, ...rest }) => {
	const skeleton = <div className="gos-skeleton gos-skeleton-1-1" style={{ width: 100, height: 21 }} />;

	if (forceResolve && !value) {
		// return <></>;
		return <KeyValue value={"-"} {...rest} />;
	}
	return <KeyValue value={value || skeleton} {...rest} />;
};

export default function CampaignInformationRibbon({
	// this comment exists to prevent eslint from making this unreadable.
	activationCampaignID,
	masterCampaignID,
	metaData,
	availableActivations,
	templateExists,
	onUpdate,
	fetchMetaData
}) {
	const [platformAPIData, setPlatformAPIData] = useState({});
	const [isUSCurrency, setIsUSCurrency] = useState(false);

	useEffect(() => {
		MediaMath.determineIfUSCurrency([AppSDK.getContext()], () => {
			setIsUSCurrency(true);
		});
	}, []);

	useEffect(() => {
		const updatePlatformAPIData = () => {
			const statues = BookFiltersStore.getSyncStatuses();
			const { PlatformName, ExternalSystemStatusID, ExternalSystemResponse } = metaData;
			const PLATFORM_MAP = {
				"Mediaocean (OX)/Prisma": { code: 5, label: "Prisma" },
				DV360: { code: 2, label: "DV360" },
				"Google Campaign Manager": { code: 1, label: "GCM" }
			};
			const { code, label } = PLATFORM_MAP[PlatformName] || {};
			const { SyncStatus } = statues?.find(({ MasterPlatformID }) => MasterPlatformID == code) || {};

			if (SyncStatus || DEBUG_MODE) {
				setPlatformAPIData({
					label,
					enabled: SyncStatus,
					status: ExternalSystemStatusID,
					tooltip: ExternalSystemResponse
				});
			} else {
				setPlatformAPIData({});
			}
		};

		updatePlatformAPIData();
		BookFiltersStore.on("fetchSyncStatusesSuccess", updatePlatformAPIData);
		BookFiltersStore.on("fetchSyncStatusesFailure", updatePlatformAPIData);

		return () => {
			BookFiltersStore.removeListener("fetchSyncStatusesSuccess", updatePlatformAPIData);
			BookFiltersStore.removeListener("fetchSyncStatusesFailure", updatePlatformAPIData);
		};
	}, [metaData]);

	const budgetValue = useMemo(() => {
		if (metaData?.RemainingBudget) {
			const currencySymbol = isUSCurrency ? BookFiltersStore.getCurrencySymbol() || "$" : "";
			const v = parseFloat(metaData?.RemainingBudget.toString().replace(/[^\d.-]/g, ""))
				.toFixed(2)
				.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
			return `${currencySymbol} ${v}`;
		}
		return null;
	}, [metaData, isUSCurrency]);

	const taxonomyValue = useMemo(() => {
		if (metaData?.CampaignUniqueIDCode && metaData?.TX) {
			return <TaxonomyCellRenderer taxonomy={metaData?.TX} isPrisma={metaData?.PlatformID === 5} />;
		}
		return null;
	}, [metaData]);

	const platformValue = useMemo(() => {
		return <PlatformStatusIcon status={platformAPIData.status} tooltip={platformAPIData.tooltip} isBadge={false} />;
	}, [platformAPIData]);

	const forceResolve = useMemo(() => {
		return Object.keys(metaData)?.length > 0;
	}, [metaData]);

	return (
		<>
			<KeyValueSet
				// this comment exists to prevent eslint from making this unreadable.
				className={`${classNameComponent} ${platformAPIData.enabled || DEBUG_MODE ? "seven-column-layout" : "six-column-layout"}`}
				display={KEY_VALUE_SET_DISPLAY.STACK}
				layout={KEY_VALUE_SET_LAYOUT.HORIZONTAL}
			>
				<KeyValue
					className={`${classNamePrefix}campaign-dropdown`}
					value={
						<Dropdown
							// this comment exists to prevent eslint from making this unreadable.
							options={availableActivations}
							value={availableActivations?.find(({ value }) => value == metaData?.CampaignUniqueIDCode)}
							onChange={({ value }) => onUpdate(value)}
							placeholder={"Loading..."}
						>
							Campaign
						</Dropdown>
					}
				/>
				<SuspensefulKeyValue className={`${classNamePrefix}taxonomy-value`} title="Taxonomy" value={taxonomyValue} forceResolve={forceResolve} />
				{platformAPIData.enabled || DEBUG_MODE ? (
					<KeyValue className={`${classNamePrefix}platform-status-value`} title={platformAPIData.label || "Platform Status"} value={platformValue} />
				) : (
					<></>
				)}
				<SuspensefulKeyValue title="Platform" value={metaData?.PlatformName} forceResolve={forceResolve} />
				<SuspensefulKeyValue title="Start Date" value={metaData?.StartDate} forceResolve={forceResolve} />
				<SuspensefulKeyValue title="End Date" value={metaData?.EndDate} forceResolve={forceResolve} />
				<SuspensefulKeyValue title="Remaining Budget" value={budgetValue} forceResolve={forceResolve} />
			</KeyValueSet>
		</>
	);
}
